import { Building2, FileSearch, Users } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdminOverview } from '../api/admin';

/**
 * The admin sections, described by what they are for.
 *
 * Kept in step with ADMIN_NAV in admin-sidebar.tsx by hand rather than derived
 * from it: the nav needs a label short enough to sit beside an icon, and this
 * needs a sentence. Deriving one from the other would force both to be the
 * same length.
 */
const SECTIONS = [
  {
    name: 'Users',
    description:
      'Every account on the platform. New sign-ups are held here until someone approves them, so this is where an application is checked before the applicant gets in. Accounts can also be disabled and re-enabled later.',
  },
  {
    name: 'Assessments',
    description:
      'Every screening run by every customer, with its risk level and outcome. Read-only: the record of what the platform has done, not a place to change it.',
  },
  {
    name: 'Company risk',
    description:
      'The same screenings grouped by the firm the user belongs to, so risk reads at firm level rather than one person at a time.',
  },
  {
    name: 'Transactions',
    description:
      'Balance movements and card payments together, so a figure on an account can be traced back to what produced it.',
  },
  {
    name: 'Company credit',
    description:
      'Adds to a firm’s shared balance. Every user of that firm draws on the same pool, so a credit here is spendable by all of them.',
  },
] as const;

const COUNT_CARDS = [
  { key: 'users', label: 'Users', icon: Users, hint: 'Excludes deleted accounts' },
  { key: 'companies', label: 'Companies', icon: Building2, hint: 'All registered companies' },
  {
    key: 'assessments',
    label: 'Assessments',
    icon: FileSearch,
    hint: 'Excludes deleted assessments',
  },
] as const;

/**
 * Whether a failure looks like an access token issued before the caller was
 * granted admin access.
 *
 * The client decides what to show from `/auth/me`, which reads the database
 * live. The API authorises from the permissions baked into the access token at
 * sign-in. Promote somebody mid-session and the two disagree until their token
 * is reissued: they can reach the admin section and are then refused by it.
 *
 * Matched on the message rather than a status code because apiClient surfaces
 * failures as plain Errors. A false positive only ever adds a suggestion that
 * happens not to apply.
 */
function isStaleSessionError(error: unknown): boolean {
  return error instanceof Error && /forbidden/i.test(error.message);
}

/**
 * Landing page for the admin section.
 *
 * Shows the headline counts and nothing more. It exists to prove the whole
 * chain works end to end — token, guard, query, render — rather than to be a
 * dashboard; the real screens arrive in later tickets.
 */
export function AdminOverviewPage() {
  const { data, isLoading, isError, error } = useAdminOverview();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>

      <SectionGuide />
      {isError ? (
        <Card>
          <CardContent className="pt-6 space-y-2">
            <p className="text-sm text-destructive">
              Could not load platform counts
              {error instanceof Error ? `: ${error.message}` : '.'}
            </p>
            {isStaleSessionError(error) ? (
              // The client reads permissions live from /auth/me, but the API
              // authorises against the permissions baked into the access token
              // when it was issued. Someone granted admin access mid-session
              // therefore sees the admin section but gets refused by it, until
              // their token is reissued. Saying so beats a bare "Forbidden".
              <p className="text-sm text-muted-foreground">
                Your session may pre-date your admin access. Sign out and back in, then try again.
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <div data-testid="overview-counts" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {COUNT_CARDS.map((card) => (
            <Card key={card.key}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{card.label}</CardTitle>
                <card.icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <p className="text-2xl font-bold">{data?.[card.key]?.toLocaleString() ?? '—'}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">{card.hint}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * What each admin screen is for.
 *
 * Written for someone administering the platform rather than someone who built
 * it, and it names the decision each screen supports rather than its contents —
 * the sidebar label already says what is in there.
 *
 * Three things are deliberately absent. Permissions, because an administrator
 * who cannot reach a screen cannot read the sentence explaining it either. The
 * counts, because the cards below already carry them and prose that restates a
 * number goes stale the moment the number moves. And a welcome, because this is
 * a tool and the reader is already inside it.
 *
 * This names several of the same screens the count cards below do, so those
 * cards carry a `data-testid` and their tests query within it — a bare
 * `getByText('Users')` now matches twice.
 */
function SectionGuide() {
  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <p className="text-sm text-muted-foreground">
          This is the administrator's view of the platform. Everything a customer cannot see or do
          themselves lives here: vetting new sign-ups, reviewing the screening work being done, and
          topping up the balances firms draw on.
        </p>

        <dl className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
          {SECTIONS.map((section) => (
            <div key={section.name}>
              <dt className="font-medium text-foreground">{section.name}</dt>
              <dd className="text-muted-foreground">{section.description}</dd>
            </div>
          ))}
        </dl>

        <p className="text-xs text-muted-foreground">
          Approvals, rejections, account changes and credits are recorded against the administrator
          who made them.
        </p>
      </CardContent>
    </Card>
  );
}
